import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useLoginMutation } from '../../store/api/authApi';
import { setPendingAuth } from '../../store/slices/authSlice';
import GoogleSignInButton from '../common/GoogleSignInButton';
import Turnstile, { isTurnstileConfigured } from '../common/Turnstile';
import AuthShell, { AuthField, AuthSubmit, AuthAlert, AuthTabs } from './AuthShell';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileRef = useRef(null);

  // The Turnstile token is single use, so drop it and re-arm the widget
  // whenever a submit does not go through.
  const resetCaptcha = () => {
    setCaptchaToken('');
    turnstileRef.current?.reset();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (isTurnstileConfigured() && !captchaToken) {
      newErrors.captcha = 'Please complete the security check';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');

    if (!validate()) return;

    try {
      const result = await login({
        email: formData.email,
        password: formData.password,
        turnstileToken: captchaToken,
      }).unwrap();

      // Trusted device, no 2FA: the token is already on the response and the
      // auth slice stored it — go straight in, no OTP.
      if (result?.data?.trusted && result?.data?.token) {
        navigate('/onboarding');
        return;
      }

      // Trusted device with 2FA enabled: email OTP is skipped, but the
      // authenticator step still runs. Jump straight to it with the pending token.
      if (result?.data?.twoFactorRequired) {
        dispatch(setPendingAuth({ email: formData.email, type: 'login' }));
        navigate('/verify-otp', { state: { pendingToken: result.data.pendingToken } });
        return;
      }

      // Untrusted device: verify via the emailed OTP.
      dispatch(setPendingAuth({ email: formData.email, type: 'login' }));
      navigate('/verify-otp');
    } catch (err) {
      setApiError(err.data?.message || 'Login failed. Please try again.');
      resetCaptcha();
    }
  };

  return (
    <AuthShell
      width="wide"
      title="Sign in to MaxelPay"
      subtitle="Use the email address associated with your merchant account."
      panelHeading={<>Welcome back to <em>faster</em> settlement.</>}
      panelLead="Your payments, invoices and payouts are exactly where you left them."
    >
      <AuthTabs active="login" />
      {apiError && <AuthAlert>{apiError}</AuthAlert>}

      <form onSubmit={handleSubmit} noValidate>
        <div className="mp-auth-fields">
          <AuthField
            id="email"
            name="email"
            type="email"
            label="Work email"
            placeholder="you@company.com"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
          />

          <AuthField
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            placeholder="••••••••"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            trailing={
              <button
                type="button"
                className="mp-auth-eye"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            }
          />
        </div>

        <div className="mp-auth-forgot">
          <Link to="/forgot-password">Forgot password?</Link>
        </div>

        <Turnstile
          ref={turnstileRef}
          action="login"
          onVerify={(token) => {
            setCaptchaToken(token);
            setErrors((prev) => ({ ...prev, captcha: '' }));
          }}
          onExpire={() => setCaptchaToken('')}
          onError={() => setCaptchaToken('')}
        />
        {errors.captcha && <span className="mp-auth-err">{errors.captcha}</span>}

        <AuthSubmit label="Sign in" loading={isLoading} />
      </form>

      <div className="mp-auth-or">Or continue with</div>

      {/* Google's own button, rendered for real rather than hidden under a
          lookalike. The duplicate styled button that used to sit here was what
          you could see while Google's smaller one took the clicks, so the two
          never lined up. */}
      <div className="rd-social-btn-wrap">
        <GoogleSignInButton onError={setApiError} />
      </div>

      <p className="mp-auth-foot">
        Don't have an account? <Link to="/register">Sign up</Link>
      </p>
    </AuthShell>
  );
};

export default Login;
